import React from 'react';
import PropTypes from 'prop-types';

import Browser from '@wetransfer/concorde-browser';

import Store from '@app/store';
import Actions from '@app/action';
import PreviewHelper from '@app/helper/preview';

import Spinner from '@component/spinner';
import Icon from '@component/icon';

import File from './file';
import WebContent from './web-content';

export default class ItemList extends React.Component {
  static propTypes = {
    onDelete: PropTypes.func,
    onDownload: PropTypes.func,
    onPreview: PropTypes.func,
    altText: PropTypes.string,
    showPending: PropTypes.bool,
    transfer: PropTypes.object,
    maxFiles: PropTypes.number,
    previewable: PropTypes.bool,
    id: PropTypes.string,
    secret: PropTypes.string,
    recipient: PropTypes.string,
    file_id: PropTypes.string,
  };

  static defaultProps = {
    showPending: false,
    maxFiles: 0,
    previewable: false,
  };

  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);

    this.state = {
      hoverNum: null,
    };

    this.renderTransferItem = this.renderTransferItem.bind(this);
  }

  /**
   * Calls async the onDownload prop, checks for errors and updates the `downloaded` property in the state.
   *
   * @param {number} num  file index in this.props.transfer.files
   * @param {SytheticEvent} e
   */
  async onDownload(num, e) {
    e.stopPropagation();
    e.preventDefault();

    const { errors } = Store.getState();
    const { transfer } = this.props;
    await this.props.onDownload(transfer.items[num].id, e);

    // Did the download fail?
    if (errors.transfer) {
      Store.dispatch(Actions.Errors.resetFormErrors('transfer'));
      return;
    }
  }

  /**
   * Opens item link on a new tab/window
   *
   * @param  {SytheticEvent} e
   * @param  {Object}        item Transfer item
   */
  onOpenLink(e, item) {
    e.stopPropagation();
    e.preventDefault();

    const link = window.open(item.url, '_blank').focus();
    if (link) {
      link.opener = null;
    }
  }

  /**
   * Updates the hoverNum property in the component state if the browser is not a touch device.
   *
   * @param  {number} num file index
   */
  updateHoverNum(num) {
    if (Browser.supportsTouchEvents) {
      return null;
    }
    this.setState({ hoverNum: num });
  }

  /**
   * Render the single file action. Can be either a spinner, a completed icon, or a download icon
   *
   * @param  {number} num  index in this.props.transfer.files
   * @param  {number} id  file id
   *
   * @return {ReactElement} Action markup
   */
  renderDownloadAction(num, id) {
    const item = this.props.transfer.items.find((file) => file.id === id);
    if (!item || this.props.showPending) {
      return null;
    }

    const { file } = item;

    // Should we render a spinner for this file?
    if (file.state && file.state.download === 'downloading') {
      return (
        <div
          className="filelist__spinner"
          onMouseLeave={() => this.setState({ hoverNum: null })}
        >
          <Spinner
            rotate={true}
            percentage={25}
            displayText={false}
            circumference={24}
            stroke={2}
          />
        </div>
      );
    }

    // Or is this a delete action?
    if (this.props.onDelete) {
      return (
        <div
          className="filelist__action filelist__action--delete"
          onClick={(e) => this.props.onDelete(num, e)}
        >
          <Icon type="Remove" />
        </div>
      );
    }

    // Maybe it's downloadable?
    if (this.props.onDownload) {
      let icon = <Icon type="DownloadSmall" />;
      const className = ['filelist__action'];

      if (
        file.state &&
        file.state.download === 'done' &&
        this.state.hoverNum !== num
      ) {
        icon = <Icon type="CheckCircle" />;
        className.push('filelist__action--downloaded');
      }

      return (
        <div
          className={className.join(' ')}
          onClick={(e) => this.onDownload(num, e)}
          onMouseOver={() => this.updateHoverNum(num)}
          onMouseLeave={() => this.updateHoverNum(null)}
        >
          {icon}
        </div>
      );
    }

    return null;
  }

  renderPreviewAction() {
    return (
      <div className="filelist__action">
        <Icon type="SearchCircle" />
      </div>
    );
  }

  renderOpenLinkAction(item) {
    const className = ['filelist__action', 'filelist__action--link'];

    return (
      <div className={className.join(' ')}>
        <Icon type="OpenLink" onClick={(e) => this.onOpenLink(e, item)} />
      </div>
    );
  }

  /**
   * Render the transfer item based on its type
   * @param  {Object}       Transfer object to render
   * @param  {number}       Position of the item on the list of files
   * @return {ReactElement} Transfer item
   */
  renderTransferItem(item, num) {
    const {
      id,
      secret,
      recipient,
      maxFiles,
      file_id,
      showPending,
    } = this.props;
    if (maxFiles && num >= maxFiles) {
      return null;
    }

    let onClick = null;
    if (!showPending && item.previewable) {
      onClick = () => PreviewHelper.goToPreview(id, secret, recipient, item.id);
    }

    const active =
      item.id === file_id && !!Store.getState().route.path.match(/download/);
    let fileAction = null;

    switch (item.content_identifier) {
      case 'web_content':
        return (
          <WebContent
            onClick={onClick}
            active={active}
            item={item}
            action={this.renderOpenLinkAction(item)}
          />
        );
      case 'file': {
        if (item.previewable && !Browser.isMobile) {
          fileAction = this.renderPreviewAction(item.id);
        } else {
          fileAction = this.renderDownloadAction(num, item.id);
        }

        return (
          <File
            onClick={onClick}
            active={active}
            file={item}
            altText={this.props.altText}
            showPending={this.props.showPending}
            action={fileAction}
            key={item.name}
          />
        );
      }
      default:
        return null;
    }
  }

  // Render the list of items.
  render() {
    const { transfer } = this.props;
    const classNames = ['filelist'];

    if (this.props.showPending) {
      classNames.push('filelist--pending');
    }

    if (this.props.previewable) {
      classNames.push('filelist--support-previews');
    }

    if (!transfer.items || !transfer.items.length) {
      return null;
    }

    return (
      <ul className={classNames.join(' ')}>
        {transfer.items.map(this.renderTransferItem)}
      </ul>
    );
  }
}
